import { useState, useEffect, useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from '@/assets/logo.png';
import { Button, Heading, Paragraph, Spinner } from '@/components/atoms';
import { TextInput } from '@/components/atoms/TextInput';
import { AuthForm } from '@/components/molecules';
import { useIntl } from '@/intl';
import environmentService from '@/services/environment.service';

interface SsoCompanyResponse {
  company_id: string;
  company_name: string;
  sso_enabled: boolean;
  sso_type: string;
  domain: string;
}

const SSO: React.FC = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [email, setEmail] = useState(location.state?.email || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [ssoCompany, setSsoCompany] = useState<SsoCompanyResponse | null>(null);

  // Check if the email domain is associated with an SSO provider
  const checkSsoDomain = useCallback(async () => {
    if (!email?.includes('@')) {
      setError(formatMessage({ id: 'shared.errors.invalidEmail' }));
      return;
    }

    setLoading(true);
    setError(undefined);

    try {
      let recaptchaToken = '';
      const requiresRecaptcha = environmentService.recaptchaKey.length > 0;

      if (requiresRecaptcha) {
        if (!executeRecaptcha) {
          throw new Error('Recaptcha not loaded');
        }

        recaptchaToken = await executeRecaptcha('sso_resolve');
        if (!recaptchaToken) {
          throw new Error('Recaptcha token not found');
        }
      }

      const response = await fetch(
        `${environmentService.baseUrlAuth}/auth/v2/sso/resolve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email, recaptcha_token: recaptchaToken })
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          setError(formatMessage({ id: 'sso.errors.noSsoConfiguration' }));
        } else if (response.status === 403) {
          setError(formatMessage({ id: 'shared.errors.generic' }));
        } else if (response.status === 429) {
          setError(formatMessage({ id: 'shared.errors.generic' }));
        } else {
          setError(formatMessage({ id: 'shared.errors.generic' }));
        }
        setLoading(false);
        return;
      }

      const data = (await response.json()) as SsoCompanyResponse;
      setSsoCompany(data);
    } catch (e) {
      setError(formatMessage({ id: 'shared.errors.generic' }));
      setLoading(false);
    }
  }, [email, executeRecaptcha, formatMessage]);

  // Initiate SSO flow
  const initiateSso = useCallback(async () => {
    if (!ssoCompany) return;

    try {
      let recaptchaToken = '';
      const requiresRecaptcha = environmentService.recaptchaKey.length > 0;

      if (requiresRecaptcha) {
        if (!executeRecaptcha) {
          throw new Error('Recaptcha not loaded');
        }

        recaptchaToken = await executeRecaptcha('sso_initiate');
      }

      const redirectUri = `${environmentService.appUrl}/token_handler`;

      window.location.href = `${environmentService.baseUrlAuth}/auth/v2/sso/${
        ssoCompany.company_id
      }/initiate?redirect_uri=${encodeURIComponent(
        redirectUri
      )}&recaptcha_token=${recaptchaToken}`;
    } catch (e) {
      setError(formatMessage({ id: 'shared.errors.generic' }));
      setLoading(false);
    }
  }, [ssoCompany, executeRecaptcha, formatMessage]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void checkSsoDomain();
  };

  // If we already have the company info, initiate SSO
  useEffect(() => {
    if (ssoCompany) {
      void initiateSso();
    }
  }, [ssoCompany, initiateSso]);

  const goBack = () => {
    navigate(email ? `/login?email=${encodeURIComponent(email)}` : '/login');
  };

  return (
    <AuthForm
      onSubmit={handleSubmit}
      shellTopLeft={
        <img src={Logo} alt='Vamoz' className='h-6 w-auto sm:h-10' />
      }>
      <div className='mx-auto w-full max-w-[28rem] -translate-y-8 space-y-6 sm:max-w-[30rem] sm:translate-y-0 sm:space-y-10'>
        <div className='space-y-4 text-center'>
          <Heading
            type='h1'
            className='mx-auto max-w-lg text-3xl font-semibold leading-[1.05] tracking-tight text-black sm:text-5xl'>
            {formatMessage({ id: 'sso.title' }) || 'Login with SSO'}
          </Heading>

          <Paragraph className='text-black/65 mx-auto max-w-xl px-3 text-sm leading-6 sm:px-0 sm:text-base sm:leading-7'>
            {formatMessage({ id: 'sso.subtitle' }) ||
              'Enter your company email to continue with Single Sign-On'}
          </Paragraph>
        </div>

        {loading ? (
          <div className='bg-white/92 flex justify-center rounded-[2rem] border border-black/5 px-5 py-8 backdrop-blur sm:px-6 sm:py-12'>
            <Spinner />
          </div>
        ) : (
          <div className='bg-white/92 mx-auto w-full rounded-[2rem] border border-black/5 px-4 pb-4 pt-4 backdrop-blur sm:px-7 sm:pb-6 sm:pt-7'>
            <div className='space-y-4 sm:space-y-5'>
              <TextInput
                type='email'
                id='email'
                name='email'
                placeholder={formatMessage({
                  id: 'shared.placeholders.companyEmail'
                })}
                value={email}
                error={!!error}
                className='h-12 text-sm sm:h-14 sm:text-base'
                onChange={e => {
                  setEmail(e.target.value);
                  if (error) setError(undefined);
                }}
              />

              {error && (
                <Paragraph className='rounded-2xl border border-red/20 bg-red/10 px-4 py-3 text-sm leading-6 !text-red'>
                  {error}
                </Paragraph>
              )}

              <Button
                type='submit'
                variant='primary'
                className='h-11 w-full rounded-2xl px-5 text-sm font-medium sm:h-12 sm:text-base'>
                {formatMessage({ id: 'sso.continueButton' }) ||
                  'Continue with SSO'}
              </Button>

              <Button
                type='button'
                variant='gray'
                className='h-11 w-full rounded-2xl px-5 text-sm font-medium sm:h-12 sm:text-base'
                onClick={goBack}>
                {formatMessage({ id: 'shared.backButton' }) || 'Back to Login'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </AuthForm>
  );
};

export default SSO;
